import { useEffect, useState } from 'react'
import { Search, X, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useCommissionTiers, useCreatorPrograms, useCreatorNiches } from '@/hooks/use-creators'

const CREATOR_STATUS_LABELS: Record<string, string> = {
  prospecting: 'Prospecção',
  negotiating: 'Negociação',
  active: 'Ativo',
  paused: 'Pausado',
  churned: 'Churn',
}

const CREATOR_TYPE_LABELS: Record<string, string> = {
  creator: 'Creator',
  ugc_creator: 'UGC Creator',
  internal: 'Interno',
}

export interface CreatorFilterValues {
  search: string
  status: string
  tierId: string
  niche: string
  type: string
  programId: string
}

interface CreatorFiltersProps {
  filters: CreatorFilterValues
  onFiltersChange: (filters: CreatorFilterValues) => void
}

export function CreatorFilters({ filters, onFiltersChange }: CreatorFiltersProps) {
  const [localSearch, setLocalSearch] = useState(filters.search)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const { data: commissionTiers } = useCommissionTiers()
  const { data: programs } = useCreatorPrograms()
  const { data: niches } = useCreatorNiches()

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== filters.search) {
        onFiltersChange({ ...filters, search: localSearch })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [localSearch, filters, onFiltersChange])

  const hasBasicFilters = filters.search || filters.status !== 'all' || filters.tierId !== 'all'
  const hasAdvancedFilters =
    filters.niche !== 'all' || filters.type !== 'all' || filters.programId !== 'all'
  const hasFilters = hasBasicFilters || hasAdvancedFilters
  const advancedFilterCount = [filters.niche, filters.type, filters.programId].filter(
    (v) => v !== 'all'
  ).length

  const clearFilters = () => {
    setLocalSearch('')
    onFiltersChange({
      search: '',
      status: 'all',
      tierId: 'all',
      niche: 'all',
      type: 'all',
      programId: 'all',
    })
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Primary filters row */}
      <div className="flex flex-col sm:flex-row gap-2 w-full">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, email ou cupom..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="pl-9 bg-card shadow-sm w-full"
          />
        </div>

        <Select
          value={filters.status}
          onValueChange={(value) => onFiltersChange({ ...filters, status: value })}
        >
          <SelectTrigger className="w-full sm:w-37.5 bg-card shadow-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            {Object.entries(CREATOR_STATUS_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.tierId}
          onValueChange={(value) => onFiltersChange({ ...filters, tierId: value })}
        >
          <SelectTrigger className="w-full sm:w-45 bg-card shadow-sm">
            <SelectValue placeholder="Tier de Comissão" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tiers</SelectItem>
            {commissionTiers?.map((tier) => (
              <SelectItem key={tier.id} value={tier.id}>
                {tier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Advanced filters toggle */}
        <Button
          variant={showAdvanced || hasAdvancedFilters ? 'secondary' : 'outline'}
          size="default"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="hidden sm:flex items-center gap-2 shrink-0"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtros
          {advancedFilterCount > 0 && (
            <Badge
              variant="secondary"
              className="h-5 min-w-5 px-1 text-xs bg-primary text-primary-foreground"
            >
              {advancedFilterCount}
            </Badge>
          )}
        </Button>

        {hasFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={clearFilters}
            className="hidden sm:flex shrink-0"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Limpar filtros</span>
          </Button>
        )}
      </div>

      {/* Advanced filters (collapsible on desktop, always visible toggle on mobile) */}
      {showAdvanced && (
        <div className="flex flex-col sm:flex-row gap-2 w-full animate-in fade-in-0 slide-in-from-top-1 duration-200">
          <Select
            value={filters.type}
            onValueChange={(value) => onFiltersChange({ ...filters, type: value })}
          >
            <SelectTrigger className="w-full sm:w-40 bg-card shadow-sm">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Tipos</SelectItem>
              {Object.entries(CREATOR_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {niches && niches.length > 0 && (
            <Select
              value={filters.niche}
              onValueChange={(value) => onFiltersChange({ ...filters, niche: value })}
            >
              <SelectTrigger className="w-full sm:w-40 bg-card shadow-sm">
                <SelectValue placeholder="Nicho" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Nichos</SelectItem>
                {niches.map((niche) => (
                  <SelectItem key={niche} value={niche}>
                    {niche}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {programs && programs.length > 0 && (
            <Select
              value={filters.programId}
              onValueChange={(value) => onFiltersChange({ ...filters, programId: value })}
            >
              <SelectTrigger className="w-full sm:w-45 bg-card shadow-sm">
                <SelectValue placeholder="Programa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Programas</SelectItem>
                {programs.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Mobile: full-width clear button */}
      {hasFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full sm:hidden flex items-center gap-2"
        >
          <X className="h-4 w-4" />
          Limpar filtros
        </Button>
      )}

      {/* Mobile: advanced filters toggle */}
      <Button
        variant={showAdvanced || hasAdvancedFilters ? 'secondary' : 'outline'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="w-full sm:hidden flex items-center gap-2"
      >
        <SlidersHorizontal className="h-4 w-4" />
        Mais filtros
        {advancedFilterCount > 0 && (
          <Badge
            variant="secondary"
            className="h-5 min-w-5 px-1 text-xs bg-primary text-primary-foreground"
          >
            {advancedFilterCount}
          </Badge>
        )}
      </Button>
    </div>
  )
}
